import moment from 'moment-timezone';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Container, Row } from 'reactstrap';
import DateRangeSelector from '../../../core/components/DateRangeSelector';
import UserGroupPermissions, {
  viewPermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import { useAppContext } from '../../../providers/AppProvider';
import { selectSite } from '../../../store/authReducer';

import { selectMultiSiteSelection } from '../../site-selection/reducer';
import { fetchHourlyWashCounts } from '../../wash-counts/actions';
import { selectHourlyWashCounts } from '../../wash-counts/reducer';
import {
  getClosestsSites,
  getClosestsSitesHourlyWashCounts,
  OVERVIEW_GET_MYCHARTS_WASHCOUNTS,
  setFromDate,
  setUntilDate,
} from '../actions';
import ClaimsKpiDataGetter from '../components/ClaimsKpiDataGetters';
import CostPerCarDataGetter from '../components/CostPerCarDataGetter';
import GuestCancellationDataGetter from '../components/GuestCancellationDataGetter';
import InstaClubDataGetter from '../components/InstaClubDataGetter';
import RevenueComparisonKpiDataGetter from '../components/RevenueComparisonKpiDataGetter';
import WashCountsChartDataGetter from '../components/WashCountsChartDataGetter';
import {
  selectClosestHourlyWashCounts,
  selectFromDate,
  selectMyChartsWashCounts,
  selectUntilDate,
} from '../reducer';
import { useIntl } from 'react-intl';
import HourlyWashVisual from '../components/HourlyWashVisual';
import { fetchVisualReport, fetchReport } from '../../analytics/actions';
import { SAFETY_INTERVAL } from '../../analytics/sagas';
import DailyWashCountKpi from '../components/DailyWashCountKpi';
import Loader from '../../../core/components/Loader';
import { getApiStatus } from '../../../core/utils';
import OccupancyKpi from '../components/OccupancyKpi';
import { selectSiteSettings } from '../../settings/reducer';
import DetergentsKpiChartDataGetter from '../components/DetergentsKpiDataGetter';
import { selectDetergentPods } from '../../detergentsTemp/reducer';
import { getDetergentPods } from '../actions';

export const MyCharts = (): JSX.Element => {
  const dispatch = useDispatch(),
    intl = useIntl(),
    timeZone = useAppContext().timeZone,
    fromDate = useSelector(selectFromDate),
    untilDate = useSelector(selectUntilDate),
    multiSiteSelection = useSelector(selectMultiSiteSelection),
    hourlyWashCounts = useSelector(selectHourlyWashCounts),
    overviewWashCounts = useSelector(selectMyChartsWashCounts),
    closestHourlyWashCounts = useSelector(selectClosestHourlyWashCounts),
    overviewWashCountsLoading = useSelector((state) =>
      getApiStatus(state, OVERVIEW_GET_MYCHARTS_WASHCOUNTS)
    ),
    siteSettings = useSelector(selectSiteSettings),
    detergentPods = useSelector(selectDetergentPods),
    primarySiteId = useSelector(selectSite);

  const twoMonthSelection = {
    name: 'lastTwoMonths',
    displayName: 'Last Two Months',
    from: moment.tz(timeZone).subtract(2, 'months').startOf('month'),
    until: moment.tz(timeZone).subtract(1, 'months').endOf('month'),
  };
  const getHourlyWashCounts = () => {
    dispatch(
      fetchHourlyWashCounts(
        moment.tz(fromDate, timeZone).toISOString(),
        moment.tz(untilDate, timeZone).toISOString()
      )
    );
  };
  const getManagerReport = () => {
    dispatch(fetchVisualReport(SAFETY_INTERVAL, 'Manager-Analytics', true, 'hourlyWashCounts'));
  };
  const fetchDetergentPods = () => {
    dispatch(getDetergentPods());
  };
  useEffect(() => {
    getManagerReport();
  }, []);
  useEffect(() => {
    getManagerReport();
    fetchDetergentPods();
  }, [primarySiteId, multiSiteSelection]);
  useEffect(() => {
    getHourlyWashCounts();
  }, [multiSiteSelection, fromDate, untilDate, timeZone]);
  return (
    <Container fluid className="overview">
      <div className="date-time-overview">
        <DateRangeSelector
          dates={[fromDate, untilDate]}
          onSubmit={(dates) => {
            dispatch(setFromDate(dates.from));
            dispatch(setUntilDate(dates.until));
          }}
          includeCustomSelect={false}
          additionalOptions={[twoMonthSelection]}
          removeItems={['yearToDate', 'lastYearToDate']}
        />
      </div>
      <Row>
        <Col xs={12} lg={6}>
          <UserGroupPermissions itemRef={viewPermissionRefs.wash_count_chart}>
            <WashCountsChartDataGetter
              fromDate={fromDate}
              untilDate={untilDate}
              hourlyWashCounts={hourlyWashCounts}
              closestHourly={closestHourlyWashCounts}
            />
          </UserGroupPermissions>
          <UserGroupPermissions itemRef={viewPermissionRefs.wash_count_chart}>
            <section className="container-fluid overview-chart-card seven-day-cars-washed">
              <div className="chart-title-section">
                <div className="chart-title">{intl.formatMessage({ id: 'sevenDayWash' })}</div>
              </div>
              <div className="row">
                <div className="col-12">
                  {!overviewWashCountsLoading ? (
                    <DailyWashCountKpi washCounts={overviewWashCounts} />
                  ) : (
                    <div className="powerbi-loading">
                      <Loader />
                    </div>
                  )}
                </div>
              </div>
            </section>
          </UserGroupPermissions>
          <UserGroupPermissions itemRef={viewPermissionRefs.claims_chart}>
            <ClaimsKpiDataGetter fromDate={fromDate} untilDate={untilDate} />
          </UserGroupPermissions>
          <UserGroupPermissions itemRef={viewPermissionRefs.window_sales}>
            <InstaClubDataGetter fromDate={fromDate} untilDate={untilDate} timeZone={timeZone} />
          </UserGroupPermissions>
        </Col>
        <Col xs={12} lg={6}>
          <UserGroupPermissions itemRef={viewPermissionRefs.hourly_comparision_chart}>
            <HourlyWashVisual
              visualId={'a3110de7b0e1c3ed0370'}
              pageName={'ReportSectionde220715d8729a0eb06d'}
            />
          </UserGroupPermissions>
          <UserGroupPermissions itemRef={viewPermissionRefs.survey_chart}>
            <GuestCancellationDataGetter fromDate={fromDate} untilDate={untilDate} />
          </UserGroupPermissions>
          <UserGroupPermissions itemRef={viewPermissionRefs.detergent_wip}>
            {detergentPods.length > 0 ? (
              <DetergentsKpiChartDataGetter fromDate={fromDate} untilDate={untilDate} />
            ) : (
              <div />
            )}
          </UserGroupPermissions>
          <UserGroupPermissions itemRef={viewPermissionRefs.cost_per_car}>
            <CostPerCarDataGetter fromDate={fromDate} untilDate={untilDate} />
          </UserGroupPermissions>
          <UserGroupPermissions itemRef={viewPermissionRefs.occupancy_chart}>
            <OccupancyKpi
              hourlyWashCounts={hourlyWashCounts}
              fromDate={fromDate}
              untilDate={untilDate}
              tunnelLength={siteSettings?.tunnelLength}
            />
          </UserGroupPermissions>
          <UserGroupPermissions itemRef={viewPermissionRefs.revenue_comparison}>
            <RevenueComparisonKpiDataGetter fromDate={fromDate} untilDate={untilDate} />
          </UserGroupPermissions>
        </Col>
      </Row>
    </Container>
  );
};
export default MyCharts;
